import React, { useCallback, useEffect, useRef, useState } from 'react';
import GoogleMap, { Marker, Popup } from '../../components/maps/GoogleMap';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { getUserRole } from '../../helpers/tenantHelpers';
import Button from '../../components/ui/button';
import Badge from '../../components/ui/badge';
import RevivalHeader from '../../components/Revival/RevivalHeader';
import { count, money, pct, sentence, shortDate, words } from './salesFormat';
import '../../components/Revival/revival.css';
import './sales-ds.css';

const DEFAULT_CENTER = { lat: 26.64, lng: -81.87 };
const DEBOUNCE_MS = 600;

const PIN_COLORS = {
  active_customer: '#0EA5E9', inactive_customer: '#9CA3AF',
  lead: '#F97316', property: '#3B82F6',
  interested: '#10B981', not_home: '#F59E0B', not_interested: '#EF4444',
  callback: '#6366F1', knocked_mine: '#8B5CF6', knocked_other: '#A78BFA',
  do_not_knock: '#DC2626',
};

// Pin colours are the map's legend (categorical, one per pin kind) — the one
// place in Sales that carries its own colours. Chips show the same colour as a dot.
const FILTER_DEFS = [
  { key: 'active_customers', label: 'Active customers', pin: 'active_customer' },
  { key: 'inactive_customers', label: 'Inactive', pin: 'inactive_customer' },
  { key: 'leads', label: 'Leads', pin: 'lead' },
  { key: 'my_knocks', label: 'My knocks', pin: 'knocked_mine' },
  { key: 'other_knocks', label: 'Other reps', pin: 'knocked_other' },
  { key: 'interested', label: 'Interested', pin: 'interested' },
  { key: 'not_home', label: 'Not home', pin: 'not_home' },
  { key: 'not_interested', label: 'Not interested', pin: 'not_interested' },
  { key: 'callback', label: 'Callback', pin: 'callback' },
];
const PIN_FOR_FILTER = Object.fromEntries(FILTER_DEFS.map((f) => [f.key, f.pin]));

function classifyMarker(item) {
  if (item.kind === 'customer') return item.is_active ? 'active_customers' : 'inactive_customers';
  if (item.kind === 'lead') return 'leads';
  if (item.kind === 'knock') {
    if (item.outcome === 'interested') return 'interested';
    if (item.outcome === 'not_home') return 'not_home';
    if (item.outcome === 'not_interested') return 'not_interested';
    if (item.outcome === 'callback') return 'callback';
    return item.is_mine ? 'my_knocks' : 'other_knocks';
  }
  return 'properties';
}

export default function FieldSalesMap() {
  const navigate = useNavigate();
  const role = getUserRole();
  const isManager = ['Admin', 'Owner', 'Manager', 'admin', 'owner', 'manager'].includes(role);

  const mapRef = useRef(null);
  const debounceRef = useRef(null);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [markers, setMarkers] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(null);
  const [selected, setSelected] = useState(null);
  const [activeFilters, setActiveFilters] = useState(new Set(FILTER_DEFS.map(f => f.key)));
  const [loading, setLoading] = useState(false);
  const [repFilter, setRepFilter] = useState('');

  const fetchData = useCallback(async (lat, lng) => {
    setLoading(true);
    try {
      const params = { lat, lng, radius: 5000, days: 30 };
      if (repFilter) params.rep_id = repFilter;
      const res = await api.get('/operations/field-map/', { params });
      const d = res.data;
      const all = [
        ...(d.customers || []).map(c => ({ ...c, filterKey: c.is_active ? 'active_customers' : 'inactive_customers' })),
        ...(d.leads || []).map(l => ({ ...l, filterKey: 'leads' })),
        ...(d.knock_events || []).map(k => ({ ...k, filterKey: classifyMarker(k) })),
        ...(d.properties || []).map(p => ({ ...p, filterKey: 'properties' })),
      ].filter(m => m.lat && m.lng && Number.isFinite(m.lat) && Number.isFinite(m.lng));
      setMarkers(all);
      setStats(d.stats || {});
      setFilters(d.filters || {});
    } catch (err) {
      console.error('field-map error:', err);
    }
    setLoading(false);
  }, [repFilter]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCenter(c);
          fetchData(c.lat, c.lng);
        },
        () => fetchData(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng)
      );
    } else {
      fetchData(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng);
    }
  }, [fetchData]);

  /* Re-fetch when the map stops moving (debounced) */
  const onMoveEnd = useCallback(() => {
    if (!mapRef.current) return;
    const c = mapRef.current.getCenter();
    if (!c) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchData(c.lat, c.lng), DEBOUNCE_MS);
  }, [fetchData]);

  const toggleFilter = (key) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const visible = markers.filter(m => activeFilters.has(m.filterKey)).slice(0, 350);

  const conv = Number(stats?.total_knocked) > 0 ? pct(stats?.conversion_rate) : '—';
  const pill = (label, value) => <>{label} <b>{value}</b></>;

  return (
    <div className="rv-screen">
      <RevivalHeader
        title="Field map"
        totals={stats ? [
          pill('Customers', count(stats.total_customers_nearby)),
          pill('Leads', count(stats.total_leads_nearby)),
          pill('Knocks', count(stats.total_knocked)),
          pill('Interested', count(stats.total_interested)),
          pill('Conversion', conv),
          loading ? 'Loading…' : `${visible.length} pins`,
        ] : [loading ? 'Loading…' : '']}
        actions={isManager && filters?.reps?.length > 0 ? (
          <div className="sl-controls">
            <select value={repFilter} onChange={(e) => setRepFilter(e.target.value)} aria-label="Rep">
              <option value="">All reps</option>
              {filters.reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        ) : null}
      />

      <div className="rv-chips" role="toolbar" aria-label="Show on the map">
        {FILTER_DEFS.map((f) => {
          if (f.key === 'other_knocks' && !isManager) return null;
          const on = activeFilters.has(f.key);
          const n = markers.filter((m) => m.filterKey === f.key).length;
          return (
            <button key={f.key} type="button" className={`rv-chip${on ? ' is-active' : ''}`} aria-pressed={on} onClick={() => toggleFilter(f.key)}>
              <span className="sl-dot" style={{ '--sl-dot': PIN_COLORS[f.pin] }} />{f.label}{n > 0 ? <b> {n}</b> : null}
            </button>
          );
        })}
      </div>

      <div className="sl-map">
        <GoogleMap
          latitude={center.lat}
          longitude={center.lng}
          zoom={14}
          onLoad={(evt) => { mapRef.current = evt.target; }}
          onMoveEnd={onMoveEnd}
        >
          {visible.map((m, i) => {
            const pinColor = PIN_COLORS[m.color_key] || PIN_COLORS[PIN_FOR_FILTER[m.filterKey]] || PIN_COLORS.property;
            const size = m.kind === 'customer' && m.is_active ? 12 : 8;
            return (
              <Marker
                key={m.id || `m-${i}`}
                latitude={m.lat}
                longitude={m.lng}
                anchor="center"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  setSelected(m);
                }}
              >
                <div
                  title={m.title || m.name || m.address || ''}
                  style={{
                    width: size * 2,
                    height: size * 2,
                    borderRadius: '50%',
                    background: pinColor,
                    border: '2px solid var(--surface)',
                    cursor: 'pointer',
                    opacity: 0.9,
                  }}
                />
              </Marker>
            );
          })}

          {selected && (
            <Popup
              latitude={selected.lat}
              longitude={selected.lng}
              onClose={() => setSelected(null)}
              closeOnClick={false}
              anchor="bottom"
              offset={10}
            >
              <div className="sl-pop">
                <div>
                  {selected.kind === 'customer' ? <Badge tone={selected.is_active ? 'success' : 'neutral'}>{selected.is_active ? 'Active customer' : 'Inactive customer'}</Badge> : null}
                  {selected.kind === 'lead' ? <Badge tone="info">Lead</Badge> : null}
                  {selected.kind === 'knock' ? <Badge>{sentence(selected.outcome)}</Badge> : null}
                </div>
                <h4>{selected.title || selected.name || 'Unknown'}</h4>
                {selected.address ? <p>{selected.address}</p> : null}
                {selected.phone ? <p>{selected.phone}</p> : null}
                {Number(selected.total_revenue) > 0 ? <p className="sl-num">{money(selected.total_revenue)}</p> : null}
                {selected.last_job_date ? <p>Last job: {shortDate(selected.last_job_date)}</p> : null}
                {selected.rep_name ? <p>Rep: {selected.rep_name}</p> : null}
                {selected.pipeline_stage ? <p>Stage: {words(selected.pipeline_stage)}</p> : null}
                {selected.acquisition_source ? <p>Source: {words(selected.acquisition_source)}</p> : null}
                <div className="sl-actions">
                  {selected.kind === 'customer' && selected.id ? (
                    <Button variant="secondary" size="sm" onClick={() => navigate(`/customers/${selected.id}`)}>Open customer</Button>
                  ) : null}
                  {selected.phone ? <a className="ds-btn ds-btn--ghost ds-btn--sm" href={`tel:${selected.phone}`}>Call</a> : null}
                </div>
              </div>
            </Popup>
          )}
        </GoogleMap>
      </div>
    </div>
  );
}
